import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { supabase } from '../lib/customSupabaseClient';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('is_approved', true)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      
      // Sort to show featured reviews first, then by creation date (most recent first)
      const sortedReviews = (data || []).sort((a, b) => {
        // First sort by featured status (featured first)
        if (a.is_featured && !b.is_featured) return -1;
        if (!a.is_featured && b.is_featured) return 1;
        
        // Then sort by creation date (most recent first)
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setReviews(sortedReviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -340, // Width of one card + gap (adjusted for 10 reviews)
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 340, // Width of one card + gap (adjusted for 10 reviews)
        behavior: 'smooth'
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <section className="py-8 bg-gradient-to-br from-gray-900 via-brand-dark to-gray-800">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-400 mx-auto"></div>
            <p className="mt-2 text-gray-300 text-sm">Loading reviews...</p>
          </div>
        </div>
      </section>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <section className="py-8 bg-gradient-to-br from-gray-900 via-brand-dark to-gray-800">
      <div className="container mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="text-center mb-8"
        >
          <motion.h2 variants={itemVariants} className="text-2xl font-bold text-white mb-2">
            Customer Reviews
          </motion.h2>
          <motion.p variants={itemVariants} className="text-sm text-gray-300 max-w-xl mx-auto">
            Trusted by thousands of satisfied customers • Showing most recent approved reviews
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="relative"
        >
          {/* Navigation arrows - hidden on mobile */}
          <button
            onClick={scrollLeft}
            className="hidden sm:block absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 hover:bg-gray-700/80 text-white p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          
          <button
            onClick={scrollRight}
            className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 hover:bg-gray-700/80 text-white p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Scrollable container */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory scroll-smooth px-8 sm:px-12"
          >
            {reviews.map((review, index) => {
              const reviewDate = new Date(review.created_at);
              const isRecent = (Date.now() - reviewDate.getTime()) < (7 * 24 * 60 * 60 * 1000); // Within last 7 days
              
              return (
              <motion.div 
                key={review.id} 
                variants={itemVariants}
                className="flex-shrink-0 w-72 sm:w-80 snap-start"
              >
                  <Card className={`h-full bg-gray-800/50 border-gray-600/50 backdrop-blur-sm hover:bg-gray-800/70 transition-all duration-200 ${
                    review.is_featured 
                      ? 'ring-1 ring-yellow-400/50 bg-yellow-900/10' 
                      : isRecent 
                        ? 'ring-1 ring-green-400/30 bg-green-900/5' 
                        : ''
                  }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-1">
                        <span className="text-yellow-400 text-sm">{getRatingStars(review.rating)}</span>
                      </div>
                        <div className="flex items-center space-x-2">
                          {isRecent && !review.is_featured && (
                            <span className="px-2 py-1 bg-green-400/20 text-green-400 text-xs rounded-full font-medium">
                              Recent
                            </span>
                          )}
                          {review.is_featured && (
                            <span className="px-2 py-1 bg-yellow-400/20 text-yellow-400 text-xs rounded-full font-medium">
                              Featured
                            </span>
                          )}
                        </div>
                    </div>

                    <blockquote className="text-gray-200 text-sm mb-3 line-clamp-3">
                      "{review.review || review.review_text}"
                    </blockquote>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white text-sm">{review.name}</p>
                      </div>
                      <div className="text-xs text-gray-400">
                          <p className={isRecent ? 'text-green-400 font-medium' : ''}>
                            {reviewDate.toLocaleDateString()}
                          </p>
                        </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              );
            })}
          </div>
          
          {/* Scroll indicators - show dots for every 2-3 reviews */}
          <div className="flex justify-center mt-4 space-x-2">
            {Array.from({ length: Math.ceil(reviews.length / 3) }, (_, index) => (
              <div 
                key={index}
                className="w-2 h-2 rounded-full bg-gray-600/50 hover:bg-gray-500/70 transition-colors duration-200 cursor-pointer"
                onClick={() => {
                  if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTo({
                      left: index * 340 * 3, // Scroll by 3 cards at a time
                      behavior: 'smooth'
                    });
                  }
                }}
              />
            ))}
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={itemVariants}
          className="text-center mt-6"
        >
          <a
            href="/reviews"
            className="inline-flex items-center px-4 py-2 border border-yellow-400/50 text-sm font-medium rounded-lg text-yellow-400 bg-yellow-400/10 hover:bg-yellow-400/20 transition-colors"
          >
            View All Reviews
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ReviewsSection;
